import React, { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { StorageContext } from '../contexts/StorageContext';
import { ProjectContext } from '../contexts/ProjectContext';
import './ProjectActionsBar.css';

/**
 * Project actions bar component
 * @param {Object} props - Component props
 * @param {Function} props.onExport - Export handler
 * @param {boolean} props.showPremiumBadge - Show premium badge
 */
function ProjectActionsBar({ onExport, showPremiumBadge }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { storage } = useContext(StorageContext);
  const { project } = useContext(ProjectContext);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  
  // Toggle menu
  const toggleMenu = () => {
    setShowMenu(prev => !prev);
  };

  // Close menu when clicking outside
  const closeMenu = (e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setShowMenu(false);
    }
  };
  
  // Register click outside handler
  React.useEffect(() => {
    if (showMenu) {
      document.addEventListener('mousedown', closeMenu);
    } else {
      document.removeEventListener('mousedown', closeMenu);
    }
    
    return () => {
      document.removeEventListener('mousedown', closeMenu);
    };
  }, [showMenu]);
  
  // Export project
  const handleExport = () => {
    setShowMenu(false);
    if (onExport) {
      onExport();
    }
  };
  
  // Show project settings
  const handleSettings = () => {
    setShowMenu(false);
    navigate(`/project/${project.id}/settings`);
  };
  
  // Show delete confirmation
  const handleDeleteClick = () => {
    setShowMenu(false);
    setShowConfirm(true);
  };
  
  // Cancel delete
  const handleCancelDelete = () => {
    setShowConfirm(false);
  };
  
  // Confirm delete
  const handleConfirmDelete = async () => {
    try {
      await storage.deleteProject(project.id);
      navigate('/');
    } catch (error) {
      console.error('Error deleting project:', error);
      // Could show an error toast here
    }
  };
  
  return (
    <div className="project-actions-bar">
      {showPremiumBadge && (
        <div className="premium-badge">
          <span className="badge-icon">⭐</span>
          <span className="badge-text">Premium</span>
        </div>
      )}
      
      <div className="actions-wrapper" ref={menuRef}>
        <button
          className="actions-button"
          onClick={toggleMenu}
          aria-haspopup="true"
          aria-expanded={showMenu}
        >
          Actions <span className="button-arrow">▼</span>
        </button>
        
        {showMenu && (
          <div className="actions-menu">
            <button className="menu-item" onClick={handleExport}>
              <span className="menu-icon">📤</span> Export Project
            </button>
            <button className="menu-item" onClick={handleSettings}>
              <span className="menu-icon">⚙️</span> Project Settings
            </button>
            <div className="menu-divider"></div>
            <button className="menu-item delete-item" onClick={handleDeleteClick}>
              <span className="menu-icon">🗑️</span> Delete Project
            </button>
          </div>
        )}
      </div>
      
      {showConfirm && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm">
            <h3>Delete Project?</h3>
            <p>
              Are you sure you want to delete <strong>{project.name}</strong>?
              This action cannot be undone.
            </p>
            <div className="confirm-actions">
              <button 
                className="cancel-button" 
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
              <button 
                className="delete-button" 
                onClick={handleConfirmDelete}
              >
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ProjectActionsBar.propTypes = {
  onExport: PropTypes.func,
  showPremiumBadge: PropTypes.bool
};

ProjectActionsBar.defaultProps = {
  showPremiumBadge: false
};

export default ProjectActionsBar;
